import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Modal, Stack, Text, Box, Group, Badge, Divider, Timeline, Tabs, Anchor, TextInput, Button, VisuallyHidden } from '@mantine/core'
import { IconSparkles, IconBug, IconArrowUp, IconSearch } from '@tabler/icons-react'
import {
  FRASE_NAO_ENCONTREI, FRASE_NAO_RESUMI, novidadesApi,
  type EscopoDaBusca, type ItemDaBusca, type RespostaDaBusca, type TipoDoItem,
} from '@/api/novidades'
import { CHANGELOG, type ChangelogEntry } from '@/changelog'
import { AREAS, areasDoItem, textoDoItem, type ChangelogItem } from '@/changelogAreas'
import { useCriteriosDeArea } from '@/hooks/useCriteriosDeArea'
import { areasVisiveis, filtrarEntradas } from '@/lib/novidades/filtro'
import { useAuthStore, useCan } from '@/store/auth'
import { APP_VERSION } from '@/version'

/** `resumoVisivel === false`: a versão foi filtrada por área e o resumo pode falar do que sumiu. */
type EntradaDoAviso = ChangelogEntry & { resumoVisivel?: boolean }

/** O que a pessoa ainda não viu: `entradas` já filtradas pelas áreas dela (a aba
 * Novidades); `versoes` são todas as versões não vistas, com ou sem item dela (o selo
 * "nova" em Minhas e Todas). */
export type Novidades = { entradas: EntradaDoAviso[]; versoes: ReadonlySet<string> }

type Aba = 'novidades' | EscopoDaBusca

const MONO = '"JetBrains Mono", "Fira Code", monospace'

/** Áreas VISÍVEIS do item que têm tela: o atalho da aba "Minhas atualizações". */
function AtalhosDoItem({ item, visiveis, onNavigate }: { item: ChangelogItem; visiveis: Set<string>; onNavigate: () => void }) {
  const areas = areasDoItem(item)
    .filter((a) => visiveis.has(a))
    .map((a) => AREAS.find((x) => x.chave === a))
    .filter((a): a is (typeof AREAS)[number] & { rota: string } => !!a?.rota)
  if (areas.length === 0) return null
  return (
    <Group gap={8} mt={2}>
      {areas.map((a) => (
        <Anchor key={a.chave} component={Link} to={a.rota} size="xs" fw={600} c="var(--ai-card-title)" underline="always" onClick={onNavigate}>
          {a.rotulo}
        </Anchor>
      ))}
    </Group>
  )
}

function VersionBlock({ entry, isLatest, isNova, atalhos }: {
  entry: EntradaDoAviso
  isLatest: boolean
  isNova?: boolean
  atalhos?: { visiveis: Set<string>; onNavigate: () => void }
}) {
  const texto = (f: ChangelogItem) => (
    <>
      <Text size="xs" c="var(--ai-text-primary)" style={{ lineHeight: 1.5 }}>{textoDoItem(f)}</Text>
      {atalhos && <AtalhosDoItem item={f} visiveis={atalhos.visiveis} onNavigate={atalhos.onNavigate} />}
    </>
  )
  return (
    <Box>
      <Group gap={8} mb={8}>
        <Text fw={700} size="sm" c="var(--ai-text-primary)" style={{ fontFamily: MONO }}>
          v{entry.version}
        </Text>
        <Text size="xs" c="var(--ai-text-secondary)" style={{ fontFamily: MONO }}>{entry.date}</Text>
        {isNova && (
          <Badge size="xs" variant="outline" color="teal">nova</Badge>
        )}
        {isLatest && (
          <Badge size="xs" variant="light" color="teal">atual</Badge>
        )}
      </Group>
      {entry.resumoVisivel !== false && (
        <Text size="xs" c="var(--ai-text-secondary)" mb={10}>{entry.summary}</Text>
      )}

      {entry.features.length > 0 && (
        <Box mb={8}>
          <Group gap={4} mb={4}>
            <IconSparkles size={12} style={{ color: 'var(--ai-brand-verde)' }} />
            <Text size="xs" fw={700} c="var(--ai-text-secondary)" tt="uppercase" style={{ fontSize: 10, letterSpacing: '0.04em' }}>
              Novidades
            </Text>
          </Group>
          <Timeline bulletSize={8} lineWidth={1} color="teal" styles={{ itemBody: { paddingLeft: 8 } }}>
            {entry.features.map((f, i) => (
              <Timeline.Item key={i} bullet={<Box style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--ai-brand-verde)' }} />}>
                {texto(f)}
              </Timeline.Item>
            ))}
          </Timeline>
        </Box>
      )}

      {entry.fixes.length > 0 && (
        <Box mb={8}>
          <Group gap={4} mb={4}>
            <IconBug size={12} style={{ color: 'var(--ai-warning, #f59f00)' }} />
            <Text size="xs" fw={700} c="var(--ai-text-secondary)" tt="uppercase" style={{ fontSize: 10, letterSpacing: '0.04em' }}>
              Correções
            </Text>
          </Group>
          <Timeline bulletSize={8} lineWidth={1} color="yellow" styles={{ itemBody: { paddingLeft: 8 } }}>
            {entry.fixes.map((f, i) => (
              <Timeline.Item key={i} bullet={<Box style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--ai-warning, #f59f00)' }} />}>
                {texto(f)}
              </Timeline.Item>
            ))}
          </Timeline>
        </Box>
      )}

      {entry.improvements.length > 0 && (
        <Box mb={8}>
          <Group gap={4} mb={4}>
            <IconArrowUp size={12} style={{ color: 'var(--ai-brand-verde)' }} />
            <Text size="xs" fw={700} c="var(--ai-text-secondary)" tt="uppercase" style={{ fontSize: 10, letterSpacing: '0.04em' }}>
              Melhorias
            </Text>
          </Group>
          <Timeline bulletSize={8} lineWidth={1} color="blue" styles={{ itemBody: { paddingLeft: 8 } }}>
            {entry.improvements.map((f, i) => (
              <Timeline.Item key={i} bullet={<Box style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--ai-brand-verde)' }} />}>
                {texto(f)}
              </Timeline.Item>
            ))}
          </Timeline>
        </Box>
      )}
    </Box>
  )
}

function Lista({ list, atalhos, vazio, novas }: {
  list: EntradaDoAviso[]
  atalhos?: { visiveis: Set<string>; onNavigate: () => void }
  vazio?: string
  novas?: ReadonlySet<string>
}) {
  if (list.length === 0 && vazio) {
    return <Text size="sm" c="var(--ai-text-secondary)" pt="md">{vazio}</Text>
  }
  return (
    <Stack gap="md" pt="md">
      {list.map((entry, i) => (
        <Box key={entry.version}>
          {i > 0 && <Divider mb="md" />}
          <VersionBlock
            entry={entry}
            isLatest={entry.version === CHANGELOG[0].version}
            isNova={novas?.has(entry.version)}
            atalhos={atalhos}
          />
        </Box>
      ))}
    </Stack>
  )
}

/** Cores por nome Mantine (DESIGN_SYSTEM.md §2.3 e §14.2): verde da marca = `inovareTeal.5`,
 * atenção = `yellow`. O ícone Tabler pinta com `currentColor`, herdado do `c` do envoltório. */
/** Indexado pelo `tipo` CRU da busca (`feature`/`fix`/`improvement`, singular), e não pelas
 * chaves de `changelog.ts` que a lista local usa. */
const TIPO_DO_ITEM: Record<TipoDoItem, { rotulo: string; Icone: typeof IconSparkles; cor: string }> = {
  feature: { rotulo: 'Novidade', Icone: IconSparkles, cor: 'inovareTeal.5' },
  fix: { rotulo: 'Correção', Icone: IconBug, cor: 'yellow.6' },
  improvement: { rotulo: 'Melhoria', Icone: IconArrowUp, cor: 'inovareTeal.5' },
}

const ROTULO_DE_SECAO = { fontSize: 10, letterSpacing: '0.04em' } as const

/** Mesmo limite do backend (`TAMANHO_MAX_PERGUNTA` em `changelog_novidades/routes.py`). */
const TAMANHO_MAX_PERGUNTA = 300

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`

/** "N resultados", ou "N ou mais resultados" quando a contagem bateu no teto da busca. */
const resultadosFora = (r: RespostaDaBusca) => r.fora_do_escopo_no_teto
  ? `${r.fora_do_escopo} ou mais resultados`
  : plural(r.fora_do_escopo, 'resultado', 'resultados')

const nadaNasAreas = (r: RespostaDaBusca, escopo: EscopoDaBusca) =>
  escopo === 'minhas' && r.itens.length === 0 && r.fora_do_escopo > 0

/** A frase da resposta escrita, ou `null` quando não há (modelo fora: só a lista, §5.5). */
function fraseDaResposta(r: RespostaDaBusca, escopo: EscopoDaBusca): string | null {
  if (nadaNasAreas(r, escopo)) return `Nada nas suas áreas; há ${resultadosFora(r)} em Todas as atualizações.`
  // §5.5: a validação descartou o texto do modelo; a lista segue valendo.
  if (r.resposta_descartada) return FRASE_NAO_RESUMI
  return r.resposta
}

/** O que o leitor de tela ouve quando a busca termina. */
function anuncioDaBusca(r: RespostaDaBusca, escopo: EscopoDaBusca): string {
  const partes = [fraseDaResposta(r, escopo)]
  if (r.itens.length > 0) {
    // Na abstenção os itens são só os "mais próximos": "não encontrei" seguido de
    // "N itens encontrados" se contradiz para quem ouve.
    partes.push(r.resposta === FRASE_NAO_ENCONTREI
      ? `${plural(r.itens.length, 'item próximo, que não responde', 'itens próximos, que não respondem')} à pergunta.`
      : `${plural(r.itens.length, 'item encontrado', 'itens encontrados')}.`)
  }
  return partes.filter(Boolean).join(' ')
}

type Atalhos = { visiveis: Set<string>; onNavigate: () => void }

function ItemEncontrado({ item, atalhos }: { item: ItemDaBusca; atalhos?: Atalhos }) {
  const tipo = TIPO_DO_ITEM[item.tipo] ?? TIPO_DO_ITEM.feature
  return (
    <Box>
      <Group gap={8} mb={2} wrap="wrap">
        <Group gap={4} wrap="nowrap">
          <Box component="span" c={tipo.cor} style={{ display: 'inline-flex' }}>
            <tipo.Icone size={12} aria-hidden />
          </Box>
          <Text size="xs" fw={700} c="var(--ai-text-secondary)" tt="uppercase" style={ROTULO_DE_SECAO}>{tipo.rotulo}</Text>
        </Group>
        <Text size="xs" fw={700} c="var(--ai-text-primary)" style={{ fontFamily: MONO }}>v{item.versao}</Text>
        <Text size="xs" c="var(--ai-text-secondary)" style={{ fontFamily: MONO }}>{item.data}</Text>
      </Group>
      <Text size="xs" c="var(--ai-text-primary)" style={{ lineHeight: 1.5 }}>{item.texto}</Text>
      {atalhos && (
        <AtalhosDoItem item={{ text: item.texto, areas: item.areas }} visiveis={atalhos.visiveis} onNavigate={atalhos.onNavigate} />
      )}
    </Box>
  )
}

/** Resultado de uma busca: resposta escrita em cima, separada; itens embaixo, na ordem do backend. */
function ResultadoDaBusca({ r, escopo, atalhos, onVerEmTodas }: {
  r: RespostaDaBusca
  escopo: EscopoDaBusca
  atalhos?: Atalhos
  onVerEmTodas: () => void
}) {
  const absteve = r.resposta === FRASE_NAO_ENCONTREI
  const nada = nadaNasAreas(r, escopo)
  const frase = fraseDaResposta(r, escopo)
  const recorte = escopo === 'minhas' && (
    <Text size="xs" c="var(--ai-text-secondary)" mt={frase ? 6 : 0}>Procurei só nas suas áreas</Text>
  )
  const atalho = escopo === 'minhas' && r.fora_do_escopo > 0 && (
    <Group gap={6} mt={4}>
      {!nada && <Text size="xs" c="var(--ai-text-secondary)">Há {resultadosFora(r)} fora delas.</Text>}
      <Anchor component="button" type="button" size="xs" fw={600} c="var(--ai-card-title)" underline="always" onClick={onVerEmTodas}>
        Ver em Todas as atualizações
      </Anchor>
    </Group>
  )
  return (
    <Stack gap="md" pt="md">
      {frase ? (
        <Box
          data-testid="resposta-da-busca"
          p="sm"
          style={{ border: '1px solid var(--ai-border-medium)', borderRadius: 8, background: 'var(--ai-card-bg)' }}
        >
          <Text size="xs" fw={700} c="var(--ai-text-secondary)" tt="uppercase" mb={4} style={ROTULO_DE_SECAO}>Resposta</Text>
          <Text size="sm" c="var(--ai-text-primary)" style={{ lineHeight: 1.55, whiteSpace: 'pre-line' }}>{frase}</Text>
          {recorte}
          {atalho}
        </Box>
      ) : (
        // Modelo fora (`resposta: null`): sem caixa de resposta oca; só o recorte, quando houver, e a lista.
        (recorte || atalho) && <Box>{recorte}{atalho}</Box>
      )}

      {r.itens.length > 0 && (
        <Box data-testid="itens-da-busca">
          {absteve ? (
            <Box mb={8}>
              <Text size="xs" fw={700} c="var(--ai-text-secondary)" tt="uppercase" style={ROTULO_DE_SECAO}>
                Itens mais próximos da pergunta
              </Text>
              <Text size="xs" c="var(--ai-text-secondary)">Não respondem à pergunta; são os mais parecidos com ela.</Text>
            </Box>
          ) : (
            <Text size="xs" fw={700} c="var(--ai-text-secondary)" tt="uppercase" mb={8} style={ROTULO_DE_SECAO}>
              {plural(r.itens.length, 'item encontrado', 'itens encontrados')}
            </Text>
          )}
          <Stack gap={10}>
            {r.itens.map((item, i) => (
              <ItemEncontrado key={`${item.versao}-${item.tipo}-${i}`} item={item} atalhos={atalhos} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}

type EstadoDaBusca =
  | { fase: 'ociosa' }
  | { fase: 'esperando-areas'; escopo: EscopoDaBusca }
  | { fase: 'buscando'; escopo: EscopoDaBusca }
  | { fase: 'pronta'; r: RespostaDaBusca; escopo: EscopoDaBusca }
  | { fase: 'erro'; mensagem: string }

function mensagemDeErro(err: unknown): string {
  const status = (err as { response?: { status?: number } } | null)?.response?.status
  if (status === 429) return 'Muitas buscas em pouco tempo; tente de novo em um minuto.'
  return 'A busca não respondeu agora. A lista abaixo continua disponível.'
}

/** Notas de versão com as abas. Com `novidades` (o aviso depois de uma atualização), a
 * primeira aba é Novidades e o modal abre nela; sem versão não vista, abre em Minhas. */
function ComAbas({ onClose, novidades }: { onClose: () => void; novidades?: Novidades }) {
  const user = useAuthStore((s) => s.user)
  const can = useCan()
  const { carregado, criterios, criteriosComErro } = useCriteriosDeArea()
  const visiveis = areasVisiveis(user, can, criterios, criteriosComErro)
  const minhas = filtrarEntradas(CHANGELOG, visiveis)

  const temNovidades = !!novidades && novidades.entradas.length > 0
  const [aba, setAba] = useState<Aba>(temNovidades ? 'novidades' : 'minhas')
  const [texto, setTexto] = useState('')
  const [consulta, setConsulta] = useState('')
  const [estado, setEstado] = useState<EstadoDaBusca>({ fase: 'ociosa' })
  // Só a última busca pinta a tela. Uma busca de cada vez: pedido feito com outra em voo
  // (trocar de aba, "Ver em Todas") vai para a fila e sai quando a em voo terminar — duas
  // chamadas ao modelo em paralelo gastam modelo e o limite de 20/min (F4).
  const ultima = useRef(0)
  const emVoo = useRef<{ q: string; escopo: EscopoDaBusca } | null>(null)
  const fila = useRef<{ q: string; escopo: EscopoDaBusca; n: number } | null>(null)
  const campo = useRef<HTMLInputElement>(null)

  const disparar = (q: string, escopo: EscopoDaBusca, n: number) => {
    emVoo.current = { q, escopo }
    setEstado({ fase: 'buscando', escopo })
    const concluir = (fim: EstadoDaBusca) => {
      const voo = emVoo.current
      emVoo.current = null
      const proxima = fila.current
      fila.current = null
      if (proxima && proxima.n === ultima.current) {
        // A fila pede o mesmo que acabou de voltar (ida e volta de aba): o resultado vale.
        if (voo && proxima.q === voo.q && proxima.escopo === voo.escopo) { setEstado(fim); return }
        // Recorte mudou: o resultado velho é descartado e a busca da fila sai agora.
        dispararRef.current(proxima.q, proxima.escopo, proxima.n)
        return
      }
      if (n === ultima.current) setEstado(fim)
    }
    novidadesApi.buscar(q, escopo, visiveis).then(
      (r) => concluir({ fase: 'pronta', r, escopo }),
      (err: unknown) => concluir({ fase: 'erro', mensagem: mensagemDeErro(err) }),
    )
  }
  // A fila sai de dentro de um `.then`: lê `visiveis` da renderização mais recente, não da antiga.
  const dispararRef = useRef(disparar)
  dispararRef.current = disparar

  const executar = (q: string, escopo: EscopoDaBusca) => {
    const pergunta = q.trim()
    const n = ++ultima.current
    setConsulta(pergunta)
    fila.current = null
    if (!pergunta) {
      setEstado({ fase: 'ociosa' })
      return
    }
    // "Minhas" depende das áreas carregadas: buscar antes mandaria o recorte incompleto.
    // A busca fica pendente e o efeito abaixo a dispara quando as áreas chegarem.
    if (escopo === 'minhas' && !carregado) {
      setEstado({ fase: 'esperando-areas', escopo })
      return
    }
    if (emVoo.current) {
      fila.current = { q: pergunta, escopo, n }
      setEstado({ fase: 'buscando', escopo })
      return
    }
    disparar(pergunta, escopo, n)
  }

  useEffect(() => {
    if (carregado && estado.fase === 'esperando-areas') executar(consulta, estado.escopo)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dispara só quando as áreas chegam
  }, [carregado])

  const buscando = estado.fase === 'buscando' || estado.fase === 'esperando-areas'
  const esperaAreas = aba === 'minhas' && !carregado

  /** Envio do campo. Cada envio custa modelo: nada sai enquanto outra busca roda. Em
   * "Minhas" com as áreas ainda carregando, `executar` segura a pergunta e o efeito acima
   * a envia quando elas chegarem — o Enter não pode sumir em silêncio. */
  const enviar = () => {
    // O campo não existe na aba Novidades.
    if (buscando || aba === 'novidades') return
    executar(texto, aba)
  }

  const trocarAba = (v: string | null) => {
    const destino: Aba = v === 'todas' ? 'todas' : v === 'novidades' ? 'novidades' : 'minhas'
    // O Mantine chama `onChange` também no clique na aba já ativa: refazer a busca ali
    // pagaria outra chamada ao modelo para pintar o mesmo resultado.
    if (destino === aba) return
    setAba(destino)
    // Novidades não busca; e voltar dela para a aba cuja resposta já está pronta não
    // paga outra chamada ao modelo.
    if (destino === 'novidades' || !consulta) return
    if (estado.fase === 'pronta' && estado.escopo === destino) return
    executar(consulta, destino)
  }

  const verEmTodas = () => {
    trocarAba('todas')
    // O atalho some com o painel; o foco volta ao campo, com a pergunta ainda nele.
    campo.current?.focus()
  }

  const atalhosMinhas: Atalhos = { visiveis, onNavigate: onClose }

  const listaLocal = (escopo: EscopoDaBusca) => escopo === 'minhas'
    ? (carregado
      ? <Lista list={minhas} atalhos={atalhosMinhas} novas={novidades?.versoes} vazio="Nenhuma atualização das suas áreas." />
      : <Text size="sm" c="var(--ai-text-secondary)" pt="md">Carregando as suas áreas…</Text>)
    : <Lista list={CHANGELOG} novas={novidades?.versoes} />

  const painel = (escopo: EscopoDaBusca) => {
    if (estado.fase === 'esperando-areas') {
      return <Text size="sm" c="var(--ai-text-secondary)" pt="md">Carregando as suas áreas antes de buscar “{consulta}”…</Text>
    }
    if (estado.fase === 'buscando') {
      return <Text size="sm" c="var(--ai-text-secondary)" pt="md">Buscando “{consulta}” nas notas de versão…</Text>
    }
    if (estado.fase === 'pronta' && estado.escopo === escopo) {
      return (
        <ResultadoDaBusca
          r={estado.r}
          escopo={escopo}
          atalhos={escopo === 'minhas' ? atalhosMinhas : undefined}
          onVerEmTodas={verEmTodas}
        />
      )
    }
    if (estado.fase === 'erro') {
      return (
        <>
          <Text size="sm" c="red.6" pt="md" role="alert">{estado.mensagem}</Text>
          {listaLocal(escopo)}
        </>
      )
    }
    return listaLocal(escopo)
  }

  // Região viva montada desde o início: leitor de tela não anuncia região que nasce com o texto.
  const anuncio = estado.fase === 'pronta'
    ? anuncioDaBusca(estado.r, estado.escopo)
    : estado.fase === 'buscando' ? 'Buscando nas notas de versão.' : ''

  return (
    <Tabs value={aba} onChange={trocarAba} keepMounted={false}>
      <Tabs.List>
        {temNovidades && <Tabs.Tab value="novidades">Novidades ({novidades.entradas.length})</Tabs.Tab>}
        <Tabs.Tab value="minhas">Minhas atualizações</Tabs.Tab>
        <Tabs.Tab value="todas">Todas as atualizações</Tabs.Tab>
      </Tabs.List>
      {/* Novidades é curta e a busca do servidor só conhece Minhas e Todas: sem campo nela. */}
      {aba !== 'novidades' && <Group gap={8} pt="md" align="center" wrap="wrap">
        <TextInput
          ref={campo}
          aria-label="Buscar nas notas de versão"
          placeholder="Pergunte, ex.: o que mudou no WAF"
          leftSection={<IconSearch size={14} aria-hidden />}
          value={texto}
          maxLength={TAMANHO_MAX_PERGUNTA}
          onChange={(e) => setTexto(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); enviar() }
          }}
          style={{ flex: 1, minWidth: 180 }}
        />
        <Button variant="light" disabled={buscando} loading={estado.fase === 'buscando'} onClick={enviar}>
          Buscar
        </Button>
        {esperaAreas && <Text size="xs" c="var(--ai-text-secondary)">Carregando as suas áreas…</Text>}
      </Group>}
      <VisuallyHidden role="status" aria-live="polite" data-testid="anuncio-da-busca">{anuncio}</VisuallyHidden>
      {temNovidades && (
        <Tabs.Panel value="novidades">
          <Text size="xs" c="var(--ai-text-secondary)" pt="md">
            O que mudou nas suas áreas desde a última versão que você viu.
          </Text>
          <Lista list={novidades.entradas} atalhos={atalhosMinhas} />
        </Tabs.Panel>
      )}
      <Tabs.Panel value="minhas">{painel('minhas')}</Tabs.Panel>
      <Tabs.Panel value="todas">{painel('todas')}</Tabs.Panel>
    </Tabs>
  )
}

export function ChangelogModal({ opened, onClose, novidades }: { opened: boolean; onClose: () => void; novidades?: Novidades }) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={8}>
          <Text fw={700} size="sm">Notas de Versão</Text>
          <Badge size="xs" variant="light" color="gray" styles={{ label: { fontFamily: MONO } }}>v{APP_VERSION}</Badge>
        </Group>
      }
      size="lg"
    >
      <ComAbas onClose={onClose} novidades={novidades} />
    </Modal>
  )
}
